import React from "react";
import { format } from "date-fns";
import { omit } from "underscore";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import AuthActions from "actions/auth-actions";
import constants from "rtr-constants";
import GridTopFiltersDueDate from "components/source/new_taxonomy/grid-top-filters-due-date";
import GridTopFiltersSize from "components/source/new_taxonomy/grid-top-filters-size";
import MaternityHelper from "helpers/maternity-helper";
import { PanelNames } from "helpers/grid-top-filters-helpers";
import productsActions from "actions/products-actions";
import userProfileActions from "actions/userProfile-actions";
import { isAnonymous } from "components/source/hoc/with-user-data";
import { HEAP_AUTH_TRIGGER_TYPES } from "helpers/heap-helpers";
import { parseISOWithoutTime, parseFormDate } from "helpers/date-helpers";
import { compose } from "redux";
import { withPersistedFilters } from "../hoc/with-persisted-filters";

const modalName = "grid-top-filters-size-modal";
const inputFormat = constants.dateFnsFormats.MM__DD__YYYY;

export class GridTopFiltersSizeAndMaternity extends React.Component {
  static propTypes = {
    changeFilters: PropTypes.func.isRequired,
    clearPreference: PropTypes.func.isRequired,
    ctaText: PropTypes.string,
    defaultSizeDekText: PropTypes.string,
    defaultSizeHedText: PropTypes.string,
    onClickCancel: PropTypes.func,
    onClickMaternityText: PropTypes.func,
    onClickOptOut: PropTypes.func,
    onClickSave: PropTypes.func,
    onSubmit: PropTypes.func,
    updateUserProfile: PropTypes.func.isRequired,
    userData: PropTypes.object,
    workingFilters: PropTypes.object,
    showAuthModal: PropTypes.func,
    writePreferences: PropTypes.func.isRequired,
  };

  static modalName = modalName;

  state = {
    currentPanel: this.getInitialPanel(),
    filtersToApply: {},
  };

  componentDidUpdate(prevProps) {
    // go back to the size panel once the due date is successfully updated
    if (
      prevProps?.userData?.userProfile?.profiles?.isLoading &&
      !this.props?.userData?.userProfile?.profiles?.isLoading
    ) {
      this.loadPanel(PanelNames.SIZE);
    }
  }

  getInitialPanel() {
    return PanelNames.SIZE;
  }

  loadPanel(nextPanel) {
    this.setState({
      currentPanel: nextPanel,
    });
  }

  saveFilters(filters) {
    const newFilters = {
      ...this.state.filtersToApply,
      ...filters,
    };
    this.setState({
      filtersToApply: newFilters,
    });
  }

  getSizeHedText() {
    if (MaternityHelper.inMaternityExperience(this.props.userData)) {
      return;
    }
    return this.props.defaultSizeHedText;
  }

  getSizeDekText() {
    if (MaternityHelper.inMaternityExperience(this.props.userData)) {
      return "We recommend renting maternity styles in your<br>usual size and other styles 1-2 sizes up.";
    }

    return this.props.defaultSizeDekText;
  }

  getMaternityText() {
    if (isAnonymous(this.props.userData)) {
      return;
    }
    const dueDate = MaternityHelper.getDueDate(this.props.userData);
    return MaternityHelper.inMaternityExperience(this.props.userData)
      ? `I'm due in ${format(parseISOWithoutTime(dueDate), constants.dateFnsFormats.MMMM)}`
      : "Expecting?";
  }

  getDueDateHedText() {
    const dueDate = MaternityHelper.getDueDate(this.props.userData);
    return MaternityHelper.inMaternityExperience(this.props.userData)
      ? `Due in ${format(parseISOWithoutTime(dueDate), constants.dateFnsFormats.MMMM)}`
      : "Congratulations!";
  }

  getDueDateDekText() {
    const editText = "Edit your due date or<br>opt out of maternity sizing below.";
    const addText = "We’ll help you find great styles for every stage.<br>When are you due?";

    return MaternityHelper.inMaternityExperience(this.props.userData) ? editText : addText;
  }

  formattedDueDate() {
    if (!MaternityHelper.inMaternityExperience(this.props.userData)) {
      return;
    }
    const dueDate = MaternityHelper.getDueDate(this.props.userData);
    return format(parseISOWithoutTime(dueDate), inputFormat);
  }

  onClickMaternityText = () => {
    this.loadPanel(PanelNames.DUE_DATE);
    this.props.onClickMaternityText();
  };

  addMaternityFilters() {
    const maternityFilters = constants.allMaternityFilters;
    const selectedFilters = {
      ...this.props.workingFilters,
      ...maternityFilters,
    };
    this.props.changeFilters(selectedFilters);
    this.props.writePreferences(selectedFilters);
    MaternityHelper.setLocalStorageAutoApplyMaternityFilters();
  }

  removeMaternityFilters() {
    const maternityFilters = constants.filters.maternity;
    const { workingFilters } = this.props;

    const selectedFilters = omit(workingFilters, maternityFilters);
    this.props.changeFilters(selectedFilters);
    this.props.clearPreference(constants.filters.maternity);
  }

  updateUserDueDate(dueDate) {
    //Default serialization of Date works fine here
    const parsedDueDate = parseFormDate(dueDate);
    const userProfile = { dueDate: parsedDueDate };
    this.props.updateUserProfile(userProfile, this.handleFailure.bind(this, this.updateUserDueDate));
  }

  removeUserDueDate = () => {
    const userProfile = { inMaternity: false };
    this.props.updateUserProfile(userProfile, this.handleFailure.bind(this, this.removeUserDueDate));
  };

  handleFailure = (callback, errorStatus) => {
    if (errorStatus === 401) {
      this.props.showAuthModal({ callback: callback, triggeredBy: HEAP_AUTH_TRIGGER_TYPES.AUTH_PROMPT });
    }
  };

  onChangeSizes(sizeFilters) {
    const newFilters = {
      ...this.state.filtersToApply,
      ...sizeFilters,
    };
    this.setState({
      filtersToApply: newFilters,
    });
  }

  onClickCancel = () => {
    this.loadPanel(PanelNames.SIZE);
    if (this.props.onClickCancel) {
      this.props.onClickCancel();
    }
  };

  onClickOptOut = () => {
    const newFilters = omit(this.state.filtersToApply, "maternity");

    this.removeUserDueDate();
    this.removeMaternityFilters();
    this.saveFilters(newFilters);
    if (this.props.onClickOptOut) {
      this.props.onClickOptOut();
    }
  };

  onClickSave = dueDate => {
    const { allMaternityFilters } = constants;
    this.updateUserDueDate(dueDate);
    if (!this.props.workingFilters.maternity) {
      this.addMaternityFilters();
      this.saveFilters(allMaternityFilters);
    }
    if (this.props.onClickSave) {
      this.props.onClickSave(dueDate);
    }
  };

  onSubmit = () => {
    const newFilters = {
      ...this.props.workingFilters,
      ...this.state.filtersToApply,
    };
    this.props.onSubmit(newFilters);

    // reset filtersToApply state
    this.setState({
      filtersToApply: {},
    });
  };

  render() {
    const xIndex = this.state.currentPanel === PanelNames.SIZE ? 0 : -100;
    const transformation = `translate3d(${xIndex}%, 0, 0)`;
    const style = {
      transform: transformation,
    };

    return (
      <div className="grid-top-filters-size-and-maternity">
        <div className="grid-top-filters-size-and-maternity-wrapper">
          <div className="grid-top-filters-size-and-maternity__panels" style={style}>
            <div className="grid-top-filters-size-and-maternity__panel">
              <GridTopFiltersSize
                dekText={this.getSizeDekText()}
                hedText={this.getSizeHedText()}
                maternityText={this.getMaternityText()}
                onChangeSizes={sizeFilters => {
                  this.onChangeSizes(sizeFilters);
                }}
                onClickMaternityText={this.onClickMaternityText}
                onSubmit={this.onSubmit}
                userData={this.props.userData}
                workingFilters={this.props.workingFilters}
                ctaText={this.props.ctaText}
              />
            </div>
            <div className="grid-top-filters-size-and-maternity__panel">
              <GridTopFiltersDueDate
                dekText={this.getDueDateDekText()}
                dueDate={this.formattedDueDate()}
                editDueDate={MaternityHelper.inMaternityExperience(this.props.userData)}
                hedText={this.getDueDateHedText()}
                onClickCancel={this.onClickCancel}
                onClickOptOut={this.onClickOptOut}
                onClickSave={value => this.onClickSave(value)}
                userData={this.props.userData}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  changeFilters: selectedFilters => dispatch(productsActions.changeFilters(selectedFilters)),
  showAuthModal: options => dispatch(AuthActions.showAuthModal(options)),
  updateUserProfile: (userProfile, onFailure) => dispatch(userProfileActions.updateUserProfile(userProfile, onFailure)),
});

export default compose(withPersistedFilters, connect(null, mapDispatchToProps))(GridTopFiltersSizeAndMaternity);
